import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material'
import { path, pathOr } from 'ramda'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'

import {
  createCustomComponent as createCustomComponentQuery,
  createVailHistory as createVailHistoryQuery,
} from '@/graphql/mutations'
import { getVailHistory } from '@/graphql/queries'
import { useListHistory, useMyUser, useOrgs } from '@/hooks'
import { useGetCustomComponent } from '@/screens/ComponentLibrary/hooks'
import { storeKey } from '@/screens/ComponentLibrary/hooks/CONSTANTS'
import { canUseName, queryClient, required } from '@/utils'
import { AmplifyGraphQLclient } from '@/utils/amplify'
import {
  ComponentType,
  CreateCustomComponentInput,
  CreateVailHistoryInput,
  CustomComponent,
  VailHistory,
} from '@/utils/api'

import { minLength, VailSelect, VailTextField } from '../reactHookForm'
import {
  SupportedDuplicateTypes,
  useDuplicateComponentData,
  useResetConfig,
} from './duplicateConfig'

export type DuplicateComponentProps = {
  id: string
  type: ComponentType
}
export type FormData = {
  version?: string | null
  org: string
  name: string
  description: string
}
type Dependency = {
  position: string
  signature?: { componentId: string; versionId: string }
  isArray?: boolean
}

export const supportedComponentsRegistry: Record<
  SupportedDuplicateTypes,
  { supported: true; dependencies?: Dependency[] } | { supported: false }
> = {
  [ComponentType.authCode]: { supported: true },
  [ComponentType.messageAssimilation]: { supported: true },
  [ComponentType.homeBase]: {
    dependencies: [
      {
        position: 'settings.customRecipeOutro.version',
        signature: {
          componentId: 'settings.customRecipeOutro.name',
          versionId: 'settings.customRecipeOutro.version',
        },
      },
    ],
    supported: true,
  },
  [ComponentType.dataIntake]: {
    dependencies: [
      {
        isArray: true,
        position: 'settings.customRecipes',
      },
    ],
    supported: true,
  },
  [ComponentType.inlineSignup]: { supported: true },
  [ComponentType.introduction]: { supported: true },
  [ComponentType.recipe]: { supported: true },
  [ComponentType.semanticSearch]: { supported: true },
  [ComponentType.sentimentCheck]: { supported: true },
  [ComponentType.taskTracker]: {
    dependencies: [
      {
        position: 'settings.customRecipeOffRamp.version',
        signature: {
          componentId: 'settings.customRecipeOffRamp.recipeName',
          versionId: 'settings.customRecipeOffRamp.version',
        },
      },
    ],
    supported: true,
  },
  [ComponentType.userGrouping]: { supported: true },
  // The ones below are not supported here because they are not standalone components
  EG: { supported: false },
  Solution: { supported: false },
  [ComponentType.SimpleApiCall]: { supported: true },
  [ComponentType.DataDump]: { supported: true },
}

/**
 * Helper function to get the component id based on the type. If the type is not supported
 * @param {ComponentType|undefined} type - The type of the component to use
 * @param {string|undefined} id - The id of the component that we want to clone
 * @returns {undefined | string} the id if the type is supported, otherwise undefined
 */
const getComponentId = (
  type?: SupportedDuplicateTypes,
  id?: string
): string | undefined => {
  if (!type) return undefined
  if (supportedComponentsRegistry[type].supported) return id
  return undefined
}

export const DuplicateComponent = () => {
  const { id, type } = useDuplicateComponentData()
  const customComponentID = getComponentId(type, id)
  const component = useGetCustomComponent(customComponentID)
  const me = useMyUser()
  const navigate = useNavigate()
  const orgs = useOrgs()
  const { orgID } = useParams()
  const closeModal = useResetConfig()

  const componentHistory = useListHistory(
    [storeKey, customComponentID, 'history'],
    'customComponent',
    { id: customComponentID }
  )

  const form = useForm<FormData>()

  useEffect(() => {
    if (
      componentHistory.data?.[0]?.id &&
      form.getValues('version') !== componentHistory.data[0].id
    )
      form.setValue('version', componentHistory.data?.[0]?.id)
  }, [form, componentHistory])

  const onSubmit = form.handleSubmit(async (data) => {
    if (!me.data?.id) return
    if (!type) return
    const res = await duplicateComponent(me.data?.id, data)
    if (res?.componentUrl) {
      closeModal()
      queryClient.invalidateQueries({ queryKey: [storeKey] })
      navigate(res.componentUrl)
    }
  })

  if (!component.data) return null

  return (
    <Dialog open={!!(type && id)} onClose={closeModal}>
      <FormProvider {...form}>
        <form onSubmit={onSubmit}>
          <DialogTitle>Duplicate from: {component.data?.name}</DialogTitle>
          <DialogContent
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <VailSelect
              control={form.control}
              defaultValue={componentHistory.data?.[0]?.id}
              label="Version"
              name="version"
              options={componentHistory.data?.map((h) => ({
                label: h.name,
                value: h.id,
              }))}
              sx={{ mt: 2 }}
            />
            <VailSelect
              control={form.control}
              defaultValue={orgID}
              label="Target Organization"
              name="org"
              options={orgs.data?.map((org) => ({
                label: org.name,
                value: org.id,
              }))}
            />
            <VailTextField
              control={form.control}
              defaultValue={`copy of ${component.data?.name}`}
              label="New Name"
              name="name"
              rules={{
                minLength: minLength(3),
                ...required,
                validate: {
                  canUseName: async (_, values) =>
                    await canUseName(values.org, undefined, values.name),
                },
              }}
            />
            <VailTextField
              multiline
              defaultValue=""
              label="New Description"
              minRows={5}
              name="description"
              rules={{ required: false }}
            />
          </DialogContent>
          <DialogActions>
            <Button variant="text" onClick={closeModal}>
              Cancel
            </Button>
            <Button loading={form.formState.isSubmitting} type="submit">
              Clone
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}

export const getSafeVailHistory = (
  historyId?: string | null
): Promise<VailHistory> => {
  if (!historyId) return Promise.resolve(null as any)
  return AmplifyGraphQLclient.graphql({
    query: getVailHistory,
    variables: { id: historyId },
  })
    .then((res) => res.data.getVailHistory)
    .catch((err) => err.data.getVailHistory)
}

const createCustomComponent = (
  input: CreateCustomComponentInput
): Promise<CustomComponent> =>
  AmplifyGraphQLclient.graphql({
    query: createCustomComponentQuery,
    variables: { input },
  })
    .then((r) => r.data.createCustomComponent)
    .catch((e) => e.data.createCustomComponent)

export const createVailHistory = (
  input: CreateVailHistoryInput
): Promise<VailHistory> =>
  AmplifyGraphQLclient.graphql({
    query: createVailHistoryQuery,
    variables: { input },
  })
    .then((r) => r.data.createVailHistory)
    .catch((e) => e.data.createVailHistory)

/**
 * This function will duplicate a component and create a new version of it it's going to be the components that don't depend on other components
 * @param {string} userId - The id of the user that is creating the component
 * @param {FormData} data - The data that the new component will have
 */
export async function duplicateComponent(userId: string, data: FormData) {
  const { version, org, name, description } = data
  const versionReq = await getSafeVailHistory(version)
  try {
    const versionData = JSON.parse(versionReq?.data)
    const newVersion: CreateCustomComponentInput = {
      ...versionData,
      ...(!!description ? { description } : {}),
      ...(!!name ? { name: name.replace('|TYPE|', versionData.type) } : {}),
      customComponentUpdatedById: userId,
      organizationCustomComponentsId: org,
    }

    const dependencies: Dependency[] = pathOr(
      [],
      [newVersion.type, 'dependencies'],
      supportedComponentsRegistry
    )

    if (dependencies.length > 0) {
      console.log('The component has dependencies')
      for (const dependency of dependencies) {
        const versionToParse: string | undefined = path(
          dependency.position.split('.'),
          versionData
        )
        // This means that no dependency was found
        if (!versionToParse) continue

        if (Array.isArray(versionToParse)) {
          console.log('we need to parse the dependency array')

          const newDependencies = await Promise.all(
            versionToParse.map(
              async ({
                type,
                version,
                componentName,
              }: {
                type: string
                version: string
                componentName: string
              }) => {
                const duplicatedComponent = await duplicateComponent(userId, {
                  description,
                  name: `|TYPE| for ${name}`,
                  org,
                  version: version,
                })
                if (!duplicatedComponent) return null

                return {
                  componentName,
                  name: duplicatedComponent.componentId,
                  type,
                  version: duplicatedComponent.historyId,
                }
              }
            )
          )
          if (versionToParse.length > 0) {
            setValueByPath(
              dependency.position,
              newDependencies.filter(Boolean),
              versionData
            )
          }
        } else {
          const dependencyPublished = await duplicateComponent(userId, {
            description,
            name: `|TYPE| for ${name}`,
            org,
            version: versionToParse,
          })
          console.log(versionData)
          if (dependency.signature?.componentId)
            setValueByPath(
              dependency.signature.componentId,
              dependencyPublished?.componentId,
              versionData
            )
          if (dependency.signature?.versionId)
            setValueByPath(
              dependency.signature.versionId,
              dependencyPublished?.historyId,
              versionData
            )
          console.log(versionData)
          console.log('we need to parse the dependency')
        }
      }
    }

    const newCustomComponent = await createCustomComponent({
      ...newVersion,
      settings: JSON.stringify(versionData.settings),
    })

    const vailHistory = await createVailHistory({
      customComponentHistoryId: newCustomComponent.id,
      data: versionReq?.data,
      name: 'v1',
      notes: 'Auto created from the copy component',
      organizationHistoryId: org,
      vailHistoryCreatedById: userId,
    })

    return {
      componentId: newCustomComponent.id,
      componentUrl: `/${org}/component-library/${newCustomComponent.id}`,
      historyId: vailHistory.id,
    }
  } catch (e) {
    console.error('Error parsing version data:', e)
    return null
  }
}

function setValueByPath(path: string, value: any, obj: object) {
  const keys = path.split('.')
  const lastKey = keys.pop()
  const target = keys.reduce((acc: any, key) => {
    // If key doesn't exist, create an empty object
    if (!acc[key]) acc[key] = {}
    return acc[key]
  }, obj)
  target[lastKey as string] = value
}
